/**
 * Broker connection config for MQTT pages.
 *
 * The legacy vertical pages each build their URL inline as
 * `wss://${host}:443/mqtt`, which hardcodes both the scheme and the port. That
 * makes it impossible to point them at a local broker, so this module adds a
 * full-URL override (`VITE_CENTRAL_WS_URL`) and falls back to the legacy shape
 * when it is absent. New pages should use this rather than building URLs inline.
 */

export interface BrokerConfig {
  /** Complete MQTT-over-WebSocket URL, ready to hand to `mqtt.connect`. */
  url: string
  username?: string
  password?: string
}

/** True when the resolved broker is a local development one. */
export function isLocalBroker(url: string): boolean {
  return /^wss?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/)/.test(url)
}

/**
 * Resolve the central broker connection.
 *
 * Precedence: an explicit `VITE_CENTRAL_WS_URL` (used by `.env.local` to reach
 * the docker-compose HiveMQ CE container) wins; otherwise the production-shaped
 * `wss://<VITE_CENTRAL_WS_ADDRESS>:443/mqtt` is used.
 */
export function resolveCentralBroker(): BrokerConfig {
  const explicitUrl = import.meta.env.VITE_CENTRAL_WS_URL
  const host = import.meta.env.VITE_CENTRAL_WS_ADDRESS

  const url = explicitUrl || `wss://${host}:443/mqtt`

  // HiveMQ CE runs without an auth extension, so empty credentials are normal
  // locally. Send undefined rather than empty strings so mqtt.js omits them.
  const username = import.meta.env.VITE_WS_USERNAME || undefined
  const password = import.meta.env.VITE_WS_PASSWORD || undefined

  return { url, username, password }
}

/** Random-suffixed client id, matching the convention used by the other pages. */
export function brokerClientId(prefix: string): string {
  return `${prefix}_${Math.random().toString(16).slice(2, 10)}`
}
